/**
 * Interactive Route Map Component
 * Real-time vehicle tracking with route optimization and layered visualization
 */

import React, { useState, useEffect, useRef, useCallback, useMemo } from 'react';
import {
  Box,
  Paper,
  IconButton,
  ToggleButton,
  ToggleButtonGroup,
  Tooltip,
  Fab,
  Menu,
  MenuItem,
  ListItemIcon,
  ListItemText,
  Chip,
  Typography,
  Slider,
  Switch,
  FormControlLabel,
  Divider,
  CircularProgress,
  Alert,
  Collapse,
  Button,
  ButtonGroup,
} from '@mui/material';
import {
  Layers,
  MyLocation,
  ZoomIn,
  ZoomOut,
  Fullscreen,
  FullscreenExit,
  Route,
  DirectionsCar,
  LocationOn,
  Traffic,
  Cloud,
  Timeline,
  Edit,
  Refresh,
  PlayArrow,
  Pause,
  Speed,
  FilterList,
  Visibility,
  VisibilityOff,
  Navigation,
  LocalShipping,
  TwoWheeler,
  DirectionsWalk,
} from '@mui/icons-material';
import { useTheme } from '@mui/material/styles';
import mapboxgl from 'mapbox-gl';
import 'mapbox-gl/dist/mapbox-gl.css';
import { useVehicles } from '../../hooks/useVehicles';
import { useLocations } from '../../hooks/useLocations';
import { useJobs } from '../../hooks/useJobs';
import { useProject } from '../../contexts/ProjectContext';
import type { Vehicle, Location, Job } from '../../types/vrp';

// Add VROOM types
interface VroomStep {
  type: 'start' | 'job' | 'end';
  id?: number;
  location: [number, number];
  arrival: number;
  service?: number;
  distance: number;
  load?: number[];
}

interface VroomResponse {
  code: number;
  summary: {
    cost: number;
    routes: number;
    unassigned: number;
    distance: number;
    duration: number;
  };
  routes: Array<{
    vehicle: number;
    cost: number;
    distance: number;
    duration: number;
    steps: VroomStep[];
  }>;
  unassigned: Array<{
    id: number;
    location: [number, number];
    reason: string;
  }>;
}
import { FallbackRouteMap } from './FallbackRouteMap';
import { 
  generateRouteFeatures,
  processRouteGeometry,
  validateCoordinates,
  type DecodedGeometry,
  type RouteGeometry
} from '../../utils/polylineDecoder';

// Initialize Mapbox with validation
const MAPBOX_TOKEN = import.meta.env.VITE_MAPBOX_TOKEN || '';
if (!MAPBOX_TOKEN) {
  console.warn('Mapbox token not found. Using fallback map component.');
}
if (MAPBOX_TOKEN) {
  mapboxgl.accessToken = MAPBOX_TOKEN;
}

interface MapLayer {
  id: string;
  name: string;
  icon: React.ReactNode;
  enabled: boolean;
  color?: string;
}

interface VehicleMarker {
  vehicle: Vehicle;
  marker: mapboxgl.Marker;
  popup: mapboxgl.Popup;
  routeLine?: mapboxgl.LngLatLike[];
  currentPosition: [number, number];
  heading: number;
  speed: number;
  status: 'idle' | 'moving' | 'stopped' | 'delayed';
}

interface InteractiveRouteMapProps {
  height?: string | number;
  showControls?: boolean;
  enableRealTimeTracking?: boolean;
  onRouteEdit?: (vehicleId: number, newRoute: unknown) => void;
  selectedVehicleId?: number;
  routeSteps?: VroomStep[];
  showRoutes?: boolean;
  viewMode?: 'overview' | 'routes' | 'tracking' | 'analytics';
  selectedOptimizationRun?: number;
  rawOptimizationResponse?: VroomResponse;
  forceStraightLines?: boolean;
  clusteringEnabled?: boolean;
  // Filtered data props (optional - will use hooks if not provided)
  vehicles?: Vehicle[];
  locations?: Location[];
  jobs?: Job[];
}

export const InteractiveRouteMap: React.FC<InteractiveRouteMapProps> = ({
  height = '600px',
  showControls = true,
  enableRealTimeTracking = true,
  onRouteEdit,
  selectedVehicleId,
  routeSteps = [],
  showRoutes = false,
  viewMode = 'overview',
  selectedOptimizationRun,
  rawOptimizationResponse,
  forceStraightLines = false,
  clusteringEnabled = true,
  vehicles: vehiclesProp,
  locations: locationsProp,
  jobs: jobsProp,
}) => {
  // Return fallback component if no Mapbox token
  if (!MAPBOX_TOKEN) {
    return <FallbackRouteMap height={height} />;
  }

  const theme = useTheme();
  // Refs
  const mapContainer = useRef<HTMLDivElement>(null);
  const map = useRef<mapboxgl.Map | null>(null);
  const vehicleMarkers = useRef<Map<number, VehicleMarker>>(new Map());

  // State
  const [isFullscreen, setIsFullscreen] = useState(false);
  const [mapStyle, setMapStyle] = useState<'streets' | 'satellite' | 'dark'>('streets');
  const [isTracking, setIsTracking] = useState(enableRealTimeTracking);
  const [playbackSpeed, setPlaybackSpeed] = useState(1);
  const [layerMenuAnchor, setLayerMenuAnchor] = useState<HTMLElement | null>(null);
  const [selectedLayer, setSelectedLayer] = useState<string | null>(null);
  
  // Memoize initial map layers to avoid recreating on each render
  const initialMapLayers = useMemo(() => [
    { id: 'routes', name: 'Routes', icon: <Route />, enabled: true, color: theme.palette.primary.main },
    { id: 'vehicles', name: 'Vehicles', icon: <DirectionsCar />, enabled: true },
    { id: 'locations', name: 'Locations', icon: <LocationOn />, enabled: true },
    { id: 'traffic', name: 'Traffic', icon: <Traffic />, enabled: false },
    { id: 'weather', name: 'Weather', icon: <Cloud />, enabled: false },
  ], [theme]);
  
  const [mapLayers, setMapLayers] = useState<MapLayer[]>(initialMapLayers);
  const [routeEditMode, setRouteEditMode] = useState(false);
  const [mapError, setMapError] = useState<string | null>(null);
  const [mapInitialized, setMapInitialized] = useState(false);

  // Data hooks (with fallback to provided props)
  const { currentProject } = useProject();
  const { data: vehiclesFromHook = [], isLoading: vehiclesLoading } = useVehicles(currentProject?.id || 0, undefined, { enabled: !vehiclesProp });
  const { data: locationsFromHook = [], isLoading: locationsLoading } = useLocations(currentProject?.id || 0, undefined, { enabled: !locationsProp });
  const { data: jobsFromHook = [], isLoading: jobsLoading } = useJobs(currentProject?.id || 0, undefined, { enabled: !jobsProp });
  
  // Use provided data or fallback to hooks
  const vehicles = vehiclesProp || vehiclesFromHook;
  const locations = locationsProp || locationsFromHook;
  const jobs = jobsProp || jobsFromHook;

  // Initialize map
  useEffect(() => {
    if (!mapContainer.current || map.current || !MAPBOX_TOKEN) {
      if (!MAPBOX_TOKEN) {
        setMapError('Mapbox token is missing. Please configure VITE_MAPBOX_TOKEN.');
      }
      return;
    }

    try {
      map.current = new mapboxgl.Map({
        container: mapContainer.current,
        style: 'mapbox://styles/mapbox/streets-v12',
        center: [28.0, -26.0], // Johannesburg, South Africa
        zoom: 6,
        pitch: 0,
        bearing: 0,
        failIfMajorPerformanceCaveat: true,
      });

      // Add navigation controls
      map.current.addControl(new mapboxgl.NavigationControl(), 'top-left');
      
      // Add scale control
      map.current.addControl(new mapboxgl.ScaleControl(), 'bottom-left');

      // Add fullscreen control
      map.current.addControl(new mapboxgl.FullscreenControl());

      // Add geolocate control
      const geolocateControl = new mapboxgl.GeolocateControl({
        positionOptions: {
          enableHighAccuracy: true,
        },
        trackUserLocation: true,
        showUserHeading: true,
      });
      map.current.addControl(geolocateControl);

      // Error handling
      map.current.on('error', (e) => {
        console.error('Mapbox error:', e);
        setMapError('Map failed to load. Please check your connection.');
      });

      // Initialize layers when map loads
      map.current.on('load', () => {
        setMapInitialized(true);
        initializeMapLayers();
      });

      return () => {
        if (map.current) {
          map.current.remove();
          map.current = null;
        }
        setMapInitialized(false);
      };
    } catch (error) {
      console.error('Failed to initialize map:', error);
      setMapError('Failed to initialize map. Please refresh the page.');
    }
  }, []);

  // Initialize map layers
  const initializeMapLayers = useCallback(() => {
    if (!map.current) return;

    console.log('🗺️ Initializing map layers');

    // Add route layer (empty until routes are optimized)
    map.current.addSource('routes', {
      type: 'geojson',
      data: {
        type: 'FeatureCollection',
        features: [], // No routes until optimization is run
      },
    });

    map.current.addLayer({
      id: 'route-lines',
      type: 'line',
      source: 'routes',
      layout: {
        'line-join': 'round',
        'line-cap': 'round',
      },
      paint: {
        'line-color': ['get', 'color'],
        'line-width': 4,
        'line-opacity': 0.8,
      },
    });

    console.log('🛣️ Routes layer initialized (empty - no routes optimized yet)');

    // Add traffic layer (if available)
    if (mapLayers.find(l => l.id === 'traffic')?.enabled) {
      map.current.addLayer({
        id: 'traffic',
        type: 'line',
        source: 'mapbox://mapbox.mapbox-traffic-v1',
        'source-layer': 'traffic',
        paint: {
          'line-width': 2,
          'line-color': [
            'match',
            ['get', 'congestion'],
            'low', '#10B981',
            'moderate', '#F59E0B',
            'heavy', '#EF4444',
            'severe', '#7C3AED',
            '#000000',
          ],
        },
      });
    }
  }, [mapLayers]);

  // Update vehicles on map
  useEffect(() => {
    if (!map.current || !mapInitialized || vehiclesLoading || !mapLayers.find(l => l.id === 'vehicles')?.enabled) return;

    console.log('📍 Adding vehicles to map:', vehicles.length, 'vehicles found');

    // Clear existing vehicle markers
    vehicleMarkers.current.forEach(({ marker, popup }) => {
      marker.remove();
      popup.remove();
    });
    vehicleMarkers.current.clear();

    // Add vehicle markers
    vehicles.forEach(vehicle => {
      console.log('🚗 Processing vehicle:', vehicle.description, 'at', vehicle.start_lat, vehicle.start_lon);
      
      if (!vehicle.start_lat || !vehicle.start_lon) {
        console.warn('⚠️ Vehicle missing coordinates:', vehicle.description);
        return;
      }

      // Create custom marker element
      const el = document.createElement('div');
      el.className = 'vehicle-marker';
      el.style.width = '40px';
      el.style.height = '40px';
      el.style.backgroundImage = getVehicleIcon(vehicle.profile);
      el.style.backgroundSize = 'contain';
      el.style.backgroundRepeat = 'no-repeat';
      el.style.cursor = 'pointer';
      el.style.transition = 'transform 0.3s ease';
      el.style.filter = 'drop-shadow(0 2px 4px rgba(0,0,0,0.3))';

      // Create marker
      const marker = new mapboxgl.Marker({
        element: el,
        anchor: 'center',
      })
        .setLngLat([vehicle.start_lon, vehicle.start_lat])
        .addTo(map.current!);

      // Create enhanced popup
      const capacityDisplay = Array.isArray(vehicle.capacity) 
        ? vehicle.capacity.join(' / ') 
        : String(vehicle.capacity || 'Not specified');

      const popup = new mapboxgl.Popup({
        offset: 25,
        closeButton: false,
      }).setHTML(`
        <div style="padding: 12px; min-width: 200px;">
          <h4 style="margin: 0 0 8px 0; font-weight: 600; color: #1f2937;">${vehicle.description}</h4>
          <p style="margin: 4px 0; font-size: 13px; line-height: 1.4; color: #4b5563;">
            <strong>Type:</strong> ${vehicle.profile || 'driving'}<br/>
            <strong>Capacity:</strong> ${capacityDisplay}<br/>
            <strong>Status:</strong> <span style="color: #10B981; font-weight: 600;">Ready</span><br/>
            <strong>Routes:</strong> <span style="color: #f59e0b;">No routes optimized yet</span>
          </p>
        </div>
      `);

      marker.setPopup(popup);

      // Add to tracking
      vehicleMarkers.current.set(vehicle.id, {
        vehicle,
        marker,
        popup,
        currentPosition: [vehicle.start_lon, vehicle.start_lat],
        heading: 0,
        speed: 0,
        status: 'idle',
      });
    });

    console.log('📍 Added', vehicleMarkers.current.size, 'vehicle markers to map');

    return () => {
      // Cleanup markers on unmount or when dependencies change
      vehicleMarkers.current.forEach(({ marker, popup }) => {
        marker.remove();
        popup.remove();
      });
      vehicleMarkers.current.clear();
    };
  }, [vehicles, vehiclesLoading, mapInitialized, mapLayers]);

  // Update locations on map
  useEffect(() => {
    if (!map.current || !mapInitialized || locationsLoading || !mapLayers.find(l => l.id === 'locations')?.enabled) return;

    console.log('📍 Adding locations to map:', locations.length, 'locations found, clustering:', clusteringEnabled);

    // Clean up existing clustered sources and layers first
    if (map.current.getSource('locations-clustered')) {
      if (map.current.getLayer('clusters')) map.current.removeLayer('clusters');
      if (map.current.getLayer('cluster-count')) map.current.removeLayer('cluster-count');
      if (map.current.getLayer('unclustered-point')) map.current.removeLayer('unclustered-point');
      map.current.removeSource('locations-clustered');
    }

    // Store references to markers for cleanup
    const locationMarkers: mapboxgl.Marker[] = [];

    // Filter locations with valid coordinates
    const validLocations = locations.filter(location => 
      location.location_lat && location.location_lon
    );

    console.log('📍 Valid locations with coordinates:', validLocations.length);

    // Add location markers with clustering - respect user's clustering preference
    if (clusteringEnabled && validLocations.length > 0) {
      // Add clustered locations
      map.current.addSource('locations-clustered', {
        type: 'geojson',
        data: {
          type: 'FeatureCollection',
          features: validLocations.map(location => ({
            type: 'Feature',
            properties: {
              id: location.id,
              name: location.name,
              type: location.location_type,
              address: location.address,
            },
            geometry: {
              type: 'Point',
              coordinates: [location.location_lon!, location.location_lat!],
            },
          })),
        },
        cluster: true,
        clusterMaxZoom: 14,
        clusterRadius: 50,
      });

      // Add cluster circles
      map.current.addLayer({
        id: 'clusters',
        type: 'circle',
        source: 'locations-clustered',
        filter: ['has', 'point_count'],
        paint: {
          'circle-color': [
            'step',
            ['get', 'point_count'],
            '#14B8A6',
            10,
            '#F59E0B',
            30,
            '#EF4444',
          ],
          'circle-radius': [
            'step',
            ['get', 'point_count'],
            20,
            10,
            30,
            30,
            40,
          ],
        },
      });

      // Add cluster count labels
      map.current.addLayer({
        id: 'cluster-count',
        type: 'symbol',
        source: 'locations-clustered',
        filter: ['has', 'point_count'],
        layout: {
          'text-field': '{point_count_abbreviated}',
          'text-font': ['DIN Offc Pro Medium', 'Arial Unicode MS Bold'],
          'text-size': 12,
        },
        paint: {
          'text-color': '#ffffff',
        },
      });
    } else {
      // Add individual location markers
      validLocations.forEach(location => {
        console.log('📍 Processing location:', location.name, 'at', location.location_lat, location.location_lon);

        const color = getLocationColor(location.location_type || 'customer');
        
        // Use custom icons for overview mode, default markers for other modes
        const markerOptions = viewMode === 'overview' 
          ? { element: createLocationMarker(location) }
          : { color, scale: 0.8 };
        
        const marker = new mapboxgl.Marker(markerOptions)
          .setLngLat([location.location_lon!, location.location_lat!])
          .setPopup(
            new mapboxgl.Popup({ offset: 25 }).setHTML(`
              <div style="padding: 12px; min-width: 200px;">
                <h4 style="margin: 0 0 8px 0; font-weight: 600; color: #1f2937;">${location.name}</h4>
                <p style="margin: 4px 0; font-size: 13px; line-height: 1.4; color: #4b5563;">
                  <strong>Type:</strong> <span style="color: ${color}; font-weight: 600; text-transform: capitalize;">${location.location_type || 'customer'}</span><br/>
                  ${location.address ? `<strong>Address:</strong> ${location.address}<br/>` : ''}
                  <strong>Jobs:</strong> <span style="color: #f59e0b;">No jobs assigned yet</span>
                </p>
              </div>
            `)
          )
          .addTo(map.current!);
        
        locationMarkers.push(marker);
      });
    }

    console.log('📍 Added', locationMarkers.length, 'location markers to map');

    return () => {
      // Cleanup markers
      locationMarkers.forEach(marker => marker.remove());
      
      // Remove clustered sources and layers if they exist
      if (map.current?.getSource('locations-clustered')) {
        if (map.current.getLayer('clusters')) map.current.removeLayer('clusters');
        if (map.current.getLayer('cluster-count')) map.current.removeLayer('cluster-count');
        map.current.removeSource('locations-clustered');
      }
    };
  }, [locations, locationsLoading, clusteringEnabled, mapLayers, mapInitialized, viewMode]);

  // Enhanced route visualization with real road geometry
  useEffect(() => {
    if (!map.current || !mapInitialized || viewMode !== 'routes' || !showRoutes || !routeSteps.length) {
      // Clear existing route visualization
      if (map.current?.getSource('route-visualization')) {
        try {
          if (map.current.getLayer('route-lines-viz')) map.current.removeLayer('route-lines-viz');
          if (map.current.getLayer('route-points-viz')) map.current.removeLayer('route-points-viz');
          if (map.current.getLayer('route-lines-preview')) map.current.removeLayer('route-lines-preview');
          map.current.removeSource('route-visualization');
        } catch (e) {
          console.warn('Error clearing route visualization:', e);
        }
      }
      return;
    }

    console.log('🛣️ Visualizing routes with enhanced geometry for optimization run:', selectedOptimizationRun, 'with', routeSteps.length, 'steps');
    console.log('🔍 [DEBUG] forceStraightLines setting:', forceStraightLines);

    try {
      // Use enhanced route generation with polyline decoding and raw VROOM response
      const { routeFeatures, pointFeatures } = generateRouteFeatures(
        routeSteps,
        vehicles,
        locations,
        rawOptimizationResponse,
        forceStraightLines
      );

      console.log('📍 Generated', routeFeatures.length, 'route lines and', pointFeatures.length, 'route points');
      console.log('🛣️ Routes with real geometry:', routeFeatures.filter(r => r.properties.hasRealGeometry).length);
      console.log('📏 Routes with straight lines:', routeFeatures.filter(r => !r.properties.hasRealGeometry).length);

      // Validate coordinates before adding to map
      const validRouteFeatures = routeFeatures.filter(feature => {
        const coords = feature.geometry.coordinates;
        const isValid = validateCoordinates(coords);
        if (!isValid) {
          console.warn('Invalid coordinates found in route feature:', feature.properties.vehicleId);
        }
        return isValid;
      });

      const validPointFeatures = pointFeatures.filter(feature => {
        const coords = feature.geometry.coordinates;
        const isValid = validateCoordinates([coords]);
        if (!isValid) {
          console.warn('Invalid coordinates found in point feature:', feature.properties.vehicleId);
        }
        return isValid;
      });

      // Add route visualization source
      map.current.addSource('route-visualization', {
        type: 'geojson',
        data: {
          type: 'FeatureCollection',
          features: [...validRouteFeatures, ...validPointFeatures]
        }
      });

      // Add main route lines layer with enhanced styling
      map.current.addLayer({
        id: 'route-lines-viz',
        type: 'line',
        source: 'route-visualization',
        filter: ['==', '$type', 'LineString'],
        layout: {
          'line-join': 'round',
          'line-cap': 'round'
        },
        paint: {
          'line-color': ['get', 'color'], // Always use the individual route color
          'line-width': [
            'case',
            ['get', 'hasRealGeometry'], 5, // Thicker for real geometry
            4 // Standard for straight lines
          ],
          'line-opacity': [
            'case',
            ['get', 'hasRealGeometry'], 0.9, // More opaque for real geometry
            forceStraightLines ? 0.8 : 0.7 // Slightly more opaque when forced
          ],
          'line-dasharray': [
            'case',
            ['get', 'hasRealGeometry'], 
              ['literal', [1]], // Solid line for real geometry
            ['literal', forceStraightLines ? [8, 4] : [2, 2]] // Much more pronounced dashes when forced straight lines
          ]
        }
      });

      // Add route preview layer (subtle outline)
      map.current.addLayer({
        id: 'route-lines-preview',
        type: 'line',
        source: 'route-visualization',
        filter: ['==', '$type', 'LineString'],
        layout: {
          'line-join': 'round',
          'line-cap': 'round'
        },
        paint: {
          'line-color': '#ffffff',
          'line-width': [
            'case',
            ['get', 'hasRealGeometry'], 8, // Outline for real geometry
            7 // Outline for straight lines
          ],
          'line-opacity': 0.3
        }
      });

      // Ensure preview layer is behind main route layer
      map.current.moveLayer('route-lines-preview', 'route-lines-viz');

      // Add enhanced route points layer
      map.current.addLayer({
        id: 'route-points-viz',
        type: 'circle',
        source: 'route-visualization',
        filter: ['==', '$type', 'Point'],
        paint: {
          'circle-color': ['get', 'color'],
          'circle-radius': [
            'case',
            ['get', 'isStart'], 10,
            ['get', 'isEnd'], 10,
            7
          ],
          'circle-stroke-width': [
            'case',
            ['get', 'isStart'], 3,
            ['get', 'isEnd'], 3,
            2
          ],
          'circle-stroke-color': '#ffffff',
          'circle-opacity': 0.95
        }
      });

      // Add click handlers for route interaction
      map.current.on('click', 'route-lines-viz', (e) => {
        if (e.features && e.features.length > 0) {
          const feature = e.features[0];
          const { vehicleId, hasRealGeometry, geometryType } = feature.properties;
          
          new mapboxgl.Popup()
            .setLngLat(e.lngLat)
            .setHTML(`
              <div style="padding: 12px; min-width: 200px;">
                <h4 style="margin: 0 0 8px 0; font-weight: 600; color: #1f2937;">Vehicle Route ${vehicleId}</h4>
                <p style="margin: 4px 0; font-size: 13px; line-height: 1.4; color: #4b5563;">
                  <strong>Geometry:</strong> <span style="color: ${hasRealGeometry ? '#10B981' : '#F59E0B'}; font-weight: 600;">${geometryType}</span><br/>
                  <strong>Type:</strong> ${hasRealGeometry ? 'Actual road routes' : 'Straight-line approximation'}<br/>
                  <strong>Mode:</strong> ${forceStraightLines ? 'Forced straight lines' : 'Auto-detected geometry'}<br/>
                  ${hasRealGeometry ? '<em>Following actual road geometry</em>' : '<em>Fallback visualization - optimize for road routes</em>'}
                  ${forceStraightLines ? '<br/><strong>Note:</strong> Straight line mode is active' : ''}
                </p>
              </div>
            `)
            .addTo(map.current!);
        }
      });

      // Add hover effects
      map.current.on('mouseenter', 'route-lines-viz', () => {
        map.current!.getCanvas().style.cursor = 'pointer';
      });

      map.current.on('mouseleave', 'route-lines-viz', () => {
        map.current!.getCanvas().style.cursor = '';
      });

      console.log(`✅ Enhanced route visualization added to map - Mode: ${forceStraightLines ? 'STRAIGHT LINES' : 'ACTUAL ROUTES'}`);
      if (forceStraightLines) {
        console.log('🌈 Straight lines mode: Look for COLORFUL DASHED lines on the map');
      }

    } catch (error) {
      console.error('❌ Error adding enhanced route visualization:', error);
    }

    return () => {
      // Cleanup route visualization
      if (map.current?.getSource('route-visualization')) {
        try {
          // Remove click handlers
          map.current.off('click', 'route-lines-viz');
          map.current.off('mouseenter', 'route-lines-viz');
          map.current.off('mouseleave', 'route-lines-viz');
          
          // Remove layers
          if (map.current.getLayer('route-lines-viz')) map.current.removeLayer('route-lines-viz');
          if (map.current.getLayer('route-lines-preview')) map.current.removeLayer('route-lines-preview');
          if (map.current.getLayer('route-points-viz')) map.current.removeLayer('route-points-viz');
          map.current.removeSource('route-visualization');
        } catch (e) {
          console.warn('Error cleaning up enhanced route visualization:', e);
        }
      }
    };
  }, [routeSteps, showRoutes, viewMode, mapInitialized, selectedOptimizationRun, vehicles, locations, rawOptimizationResponse, forceStraightLines]);

  // Auto-fit map bounds when data loads
  useEffect(() => {
    if (!map.current || !mapInitialized) return;
    
    // Don't auto-fit while data is still loading
    if (vehiclesLoading || locationsLoading) return;

    const allPoints: [number, number][] = [];

    // Add vehicle coordinates
    vehicles.forEach(vehicle => {
      if (vehicle.start_lat && vehicle.start_lon) {
        console.log('🚗 Adding vehicle point:', vehicle.description, [vehicle.start_lon, vehicle.start_lat]);
        allPoints.push([vehicle.start_lon, vehicle.start_lat]);
      }
    });

    // Add location coordinates
    locations.forEach(location => {
      if (location.location_lat && location.location_lon) {
        console.log('📍 Adding location point:', location.name, [location.location_lon, location.location_lat]);
        allPoints.push([location.location_lon, location.location_lat]);
      }
    });

    console.log('🗺️ Total points for bounds:', allPoints.length, allPoints);

    // Fit map to show all points
    if (allPoints.length > 0) {
      const bounds = new mapboxgl.LngLatBounds();
      allPoints.forEach(point => {
        console.log('📌 Extending bounds with:', point);
        bounds.extend(point);
      });
      
      console.log('🗺️ Final bounds:', bounds.getNorthEast(), bounds.getSouthWest());
      
      // Add padding and animate
      try {
        map.current.fitBounds(bounds, { 
          padding: { top: 80, bottom: 80, left: 80, right: 80 },
          duration: 1500,
          maxZoom: 15 // Prevent zooming in too close
        });
      } catch (error) {
        console.error('Error fitting bounds:', error);
        // Fallback to South Africa
        map.current.flyTo({
          center: [28.0, -26.0], // Johannesburg area
          zoom: 6,
          duration: 1000
        });
      }
    } else {
      console.log('🗺️ No data points, using default South Africa view');
      // Default to South Africa if no data
      map.current.flyTo({
        center: [28.0, -26.0], // Johannesburg area (more central)
        zoom: 6,
        duration: 1000
      });
    }
  }, [vehicles, locations, vehiclesLoading, locationsLoading, mapInitialized]);

  // Real-time vehicle tracking (disabled until routes are optimized)
  useEffect(() => {
    // No real-time tracking simulation until routes are created
    // This will be enabled once the user optimizes routes
    console.log('🚗 Real-time tracking available when routes are optimized');
    
    // For now, vehicles remain stationary at their starting positions
    return () => {};
  }, [isTracking, enableRealTimeTracking, playbackSpeed, vehicles]);

  // Helper function to create custom location marker
  const createLocationMarker = (location: Location): HTMLDivElement => {
    const el = document.createElement('div');
    el.className = 'location-marker';
    el.style.width = '32px';
    el.style.height = '32px';
    el.style.display = 'flex';
    el.style.alignItems = 'center';
    el.style.justifyContent = 'center';
    el.style.borderRadius = '50%';
    el.style.cursor = 'pointer';
    el.style.boxShadow = '0 2px 8px rgba(0,0,0,0.3)';
    el.style.transition = 'transform 0.3s ease';
    
    // Set background color and icon based on location type
    const locationType = location.location_type || 'customer';
    let backgroundColor: string;
    let iconSvg: string;
    
    switch (locationType) {
      case 'depot':
        backgroundColor = '#1976d2'; // Blue for depot
        iconSvg = `
          <svg width="20" height="20" viewBox="0 0 24 24" fill="white">
            <path d="M10 20v-6h4v6h5v-8h3L12 3 2 12h3v8z"/>
          </svg>
        `;
        break;
      case 'service_point':
        backgroundColor = '#f59e0b'; // Orange for service point
        iconSvg = `
          <svg width="20" height="20" viewBox="0 0 24 24" fill="white">
            <path d="M12 2l3.09 6.26L22 9.27l-5 4.87 1.18 6.88L12 17.77l-6.18 3.25L7 14.14 2 9.27l6.91-1.01L12 2z"/>
          </svg>
        `;
        break;
      case 'customer':
      default:
        backgroundColor = '#10b981'; // Green for customer
        iconSvg = `
          <svg width="20" height="20" viewBox="0 0 24 24" fill="white">
            <path d="M19 7h-3V6a4 4 0 0 0-8 0v1H5a1 1 0 0 0-1 1v11a3 3 0 0 0 3 3h10a3 3 0 0 0 3-3V8a1 1 0 0 0-1-1zM10 6a2 2 0 0 1 4 0v1h-4V6zm8 15a1 1 0 0 1-1 1H7a1 1 0 0 1-1-1V9h2v1a1 1 0 0 0 2 0V9h4v1a1 1 0 0 0 2 0V9h2v12z"/>
          </svg>
        `;
        break;
    }
    
    el.style.backgroundColor = backgroundColor;
    el.innerHTML = iconSvg;
    
    // Add hover effect
    el.addEventListener('mouseenter', () => {
      el.style.transform = 'scale(1.2)';
    });
    el.addEventListener('mouseleave', () => {
      el.style.transform = 'scale(1)';
    });
    
    return el;
  };

  // Helper functions
  const getVehicleIcon = (profile: string): string => {
    const icons = {
      driving: 'url(data:image/svg+xml;base64,PHN2ZyB3aWR0aD0iNDAiIGhlaWdodD0iNDAiIHZpZXdCb3g9IjAgMCA0MCA0MCIgZmlsbD0ibm9uZSIgeG1sbnM9Imh0dHA6Ly93d3cudzMub3JnLzIwMDAvc3ZnIj4KPGNpcmNsZSBjeD0iMjAiIGN5PSIyMCIgcj0iMTgiIGZpbGw9IiMxNEI4QTYiLz4KPHBhdGggZD0iTTI4IDI0VjE2QzI4IDE0LjkgMjcuMSAxNCAyNiAxNEgxNEMxMi45IDE0IDEyIDE0LjkgMTIgMTZWMjRDMTIgMjUuMSAxMi45IDI2IDE0IDI2SDE2QzE2IDI3LjEgMTYuOSAyOCAxOCAyOEMyMC4yIDI4IDIyIDI2LjIgMjIgMjRIMjZDMjcuMSAyNCAyOCAyMy4xIDI4IDIyWiIgZmlsbD0id2hpdGUiLz4KPC9zdmc+)',
      cycling: 'url(data:image/svg+xml;base64,PHN2ZyB3aWR0aD0iNDAiIGhlaWdodD0iNDAiIHZpZXdCb3g9IjAgMCA0MCA0MCIgZmlsbD0ibm9uZSIgeG1sbnM9Imh0dHA6Ly93d3cudzMub3JnLzIwMDAvc3ZnIj4KPGNpcmNsZSBjeD0iMjAiIGN5PSIyMCIgcj0iMTgiIGZpbGw9IiNGNTlFMEIiLz4KPGNpcmNsZSBjeD0iMTQiIGN5PSIyNiIgcj0iNCIgc3Ryb2tlPSJ3aGl0ZSIgc3Ryb2tlLXdpZHRoPSIyIiBmaWxsPSJub25lIi8+CjxjaXJjbGUgY3g9IjI2IiBjeT0iMjYiIHI9IjQiIHN0cm9rZT0id2hpdGUiIHN0cm9rZS13aWR0aD0iMiIgZmlsbD0ibm9uZSIvPgo8cGF0aCBkPSJNMTQgMjZMMjAgMTZMMjYgMjYiIHN0cm9rZT0id2hpdGUiIHN0cm9rZS13aWR0aD0iMiIgZmlsbD0ibm9uZSIvPgo8L3N2Zz4=)',
      foot: 'url(data:image/svg+xml;base64,PHN2ZyB3aWR0aD0iNDAiIGhlaWdodD0iNDAiIHZpZXdCb3g9IjAgMCA0MCA0MCIgZmlsbD0ibm9uZSIgeG1sbnM9Imh0dHA6Ly93d3cudzMub3JnLzIwMDAvc3ZnIj4KPGNpcmNsZSBjeD0iMjAiIGN5PSIyMCIgcj0iMTgiIGZpbGw9IiMxMEI5ODEiLz4KPGNpcmNsZSBjeD0iMjAiIGN5PSIxNCIgcj0iMyIgZmlsbD0id2hpdGUiLz4KPHBhdGggZD0iTTIwIDE5VjI4TTE2IDI4TDIwIDIzTDI0IDI4IiBzdHJva2U9IndoaXRlIiBzdHJva2Utd2lkdGg9IjIiIHN0cm9rZS1saW5lY2FwSJyb3VuZCIgZmlsbD0ibm9uZSIvPgo8L3N2Zz4=)',
    };
    return icons[profile as keyof typeof icons] || icons.driving;
  };

  const getLocationColor = useCallback((type: string): string => {
    const colors = {
      depot: theme.palette.primary.main,
      customer: theme.palette.info.main,
      service_point: theme.palette.warning.main,
    };
    return colors[type as keyof typeof colors] || theme.palette.grey[600];
  }, [theme]);

  const toggleLayer = useCallback((layerId: string) => {
    setMapLayers(prev =>
      prev.map(layer =>
        layer.id === layerId ? { ...layer, enabled: !layer.enabled } : layer
      )
    );
  }, []);

  const handleMapStyleChange = useCallback((style: 'streets' | 'satellite' | 'dark') => {
    if (!map.current) return;
    
    const styles = {
      streets: 'mapbox://styles/mapbox/streets-v12',
      satellite: 'mapbox://styles/mapbox/satellite-streets-v12',
      dark: 'mapbox://styles/mapbox/dark-v11',
    };
    
    map.current.setStyle(styles[style]);
    setMapStyle(style);
  }, []);

  const handleRouteOptimization = useCallback(() => {
    // Trigger route optimization
    console.log('Optimizing routes...');
    // This would call the optimization API
  }, []);

  const handleRefresh = useCallback(() => {
    // Refresh vehicle positions
    console.log('Refreshing vehicle positions...');
    // Force re-render of vehicle positions
  }, []);

  return (
    <Paper
      elevation={2}
      sx={{
        height,
        position: 'relative',
        overflow: 'hidden',
        borderRadius: 2,
      }}
    >
      {/* Map Container */}
      {!mapError && (
        <Box
          ref={mapContainer}
          sx={{
            width: '100%',
            height: '100%',
            position: 'absolute',
            top: 0,
            left: 0,
          }}
        />
      )}

      {/* Error Display */}
      {mapError && (
        <Alert
          severity="error"
          sx={{
            position: 'absolute',
            top: '50%',
            left: '50%',
            transform: 'translate(-50%, -50%)',
            zIndex: 1000,
            maxWidth: '80%',
          }}
          action={
            <Button
              color="inherit"
              size="small"
              onClick={() => window.location.reload()}
            >
              Reload
            </Button>
          }
        >
          {mapError}
        </Alert>
      )}

      {/* Loading Overlay */}
      {!mapError && (vehiclesLoading || locationsLoading || jobsLoading) && (
        <Box
          sx={{
            position: 'absolute',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            backgroundColor: 'rgba(255, 255, 255, 0.8)',
            zIndex: 1000,
          }}
        >
          <CircularProgress />
        </Box>
      )}

      {/* Map Controls */}
      {showControls && !mapError && (
        <>
          {/* Top Left Controls */}
          <Box
            sx={{
              position: 'absolute',
              top: 16,
              left: 16,
              display: 'flex',
              flexDirection: 'column',
              gap: 1,
              zIndex: 100,
            }}
          >
            {/* Layer Toggle */}
            <Tooltip title="Layers">
              <IconButton
                onClick={(e) => setLayerMenuAnchor(e.currentTarget)}
                sx={{
                  backgroundColor: 'white',
                  boxShadow: 2,
                  '&:hover': { backgroundColor: 'white' },
                }}
              >
                <Layers />
              </IconButton>
            </Tooltip>

            {/* Map Style Toggle */}
            <ToggleButtonGroup
              value={mapStyle}
              exclusive
              onChange={(_, value) => value && handleMapStyleChange(value)}
              orientation="vertical"
              size="small"
              sx={{
                backgroundColor: 'white',
                boxShadow: 2,
              }}
            >
              <ToggleButton value="streets">
                <Tooltip title="Streets">
                  <Route />
                </Tooltip>
              </ToggleButton>
              <ToggleButton value="satellite">
                <Tooltip title="Satellite">
                  <Navigation />
                </Tooltip>
              </ToggleButton>
              <ToggleButton value="dark">
                <Tooltip title="Dark">
                  <Visibility />
                </Tooltip>
              </ToggleButton>
            </ToggleButtonGroup>
          </Box>

          {/* Top Right Controls */}
          <Box
            sx={{
              position: 'absolute',
              top: 16,
              right: 16,
              display: 'flex',
              gap: 1,
              zIndex: 100,
            }}
          >
            {/* Route Edit Mode */}
            <Tooltip title={routeEditMode ? 'Exit Edit Mode' : 'Edit Routes'}>
              <IconButton
                onClick={() => setRouteEditMode(!routeEditMode)}
                sx={{
                  backgroundColor: routeEditMode ? theme.palette.primary.main : 'white',
                  color: routeEditMode ? 'white' : 'inherit',
                  boxShadow: 2,
                  '&:hover': { 
                    backgroundColor: routeEditMode ? theme.palette.primary.dark : 'white' 
                  },
                }}
              >
                <Edit />
              </IconButton>
            </Tooltip>

            {/* Optimize Routes */}
            <Tooltip title="Optimize Routes">
              <IconButton
                onClick={handleRouteOptimization}
                sx={{
                  backgroundColor: 'white',
                  boxShadow: 2,
                  '&:hover': { backgroundColor: 'white' },
                }}
              >
                <Route />
              </IconButton>
            </Tooltip>

            {/* Refresh */}
            <Tooltip title="Refresh">
              <IconButton
                onClick={handleRefresh}
                sx={{
                  backgroundColor: 'white',
                  boxShadow: 2,
                  '&:hover': { backgroundColor: 'white' },
                }}
              >
                <Refresh />
              </IconButton>
            </Tooltip>
          </Box>

          {/* Bottom Controls */}
          {mapInitialized && (
          <Paper
            elevation={3}
            sx={{
              position: 'absolute',
              bottom: 16,
              left: '50%',
              transform: 'translateX(-50%)',
              px: 3,
              py: 2,
              display: 'flex',
              alignItems: 'center',
              gap: 2,
              backgroundColor: 'white',
              borderRadius: 3,
              zIndex: 100,
              border: '1px solid rgba(0,0,0,0.06)',
            }}
          >
            {/* Status Indicators */}
            <Chip
              icon={<DirectionsCar />}
              label={`${vehicles.length} Vehicle${vehicles.length !== 1 ? 's' : ''}`}
              size="small"
              color="primary"
              variant="outlined"
            />

            <Chip
              icon={<LocationOn />}
              label={`${locations.length} Location${locations.length !== 1 ? 's' : ''}`}
              size="small"
              color="secondary"
              variant="outlined"
            />

            <Divider orientation="vertical" flexItem />

            {/* Route Status */}
            <Chip
              icon={<Route />}
              label={(() => {
                if (!showRoutes || !routeSteps.length) return "No Routes Optimized";
                const filteredSteps = selectedVehicleId 
                  ? routeSteps.filter(step => step.vehicle_id === selectedVehicleId)
                  : routeSteps;
                const uniqueVehicles = new Set(filteredSteps.map(step => step.vehicle_id)).size;
                const filterText = selectedVehicleId ? " (Filtered)" : "";
                return `${uniqueVehicles} Route${uniqueVehicles !== 1 ? 's' : ''} Optimized${filterText}`;
              })()}
              size="small"
              sx={{ 
                bgcolor: showRoutes && routeSteps.length > 0 ? '#D1FAE5' : '#FEF3C7', 
                color: showRoutes && routeSteps.length > 0 ? '#059669' : '#D97706',
                border: showRoutes && routeSteps.length > 0 ? '1px solid #86EFAC' : '1px solid #FCD34D',
                fontWeight: 600
              }}
            />

            {/* Route Geometry Status - Only show when routes exist */}
            {showRoutes && routeSteps.length > 0 && (
              <>
                <Chip
                  icon={<Navigation />}
                  label="Road Geometry"
                  size="small"
                  sx={{ 
                    bgcolor: '#EBF5FF', 
                    color: '#2563EB',
                    border: '1px solid #93C5FD',
                    fontWeight: 600
                  }}
                />
                <Divider orientation="vertical" flexItem />
              </>
            )}

            {/* Jobs Status */}
            <Chip
              icon={<LocalShipping />}
              label={`${jobs.length} Job${jobs.length !== 1 ? 's' : ''} Ready`}
              size="small"
              color="info"
              variant="outlined"
            />
          </Paper>
          )}

          {/* Layer Menu */}
          <Menu
            anchorEl={layerMenuAnchor}
            open={Boolean(layerMenuAnchor)}
            onClose={() => setLayerMenuAnchor(null)}
          >
            {mapLayers.map((layer) => (
              <MenuItem
                key={layer.id}
                onClick={() => toggleLayer(layer.id)}
              >
                <ListItemIcon>
                  {layer.enabled ? <Visibility /> : <VisibilityOff />}
                </ListItemIcon>
                <ListItemText primary={layer.name} />
                {layer.icon}
              </MenuItem>
            ))}
          </Menu>
        </>
      )}

      {/* Route Edit Mode Overlay */}
      <Collapse in={routeEditMode}>
        <Alert
          severity="info"
          sx={{
            position: 'absolute',
            top: 80,
            left: '50%',
            transform: 'translateX(-50%)',
            zIndex: 100,
          }}
        >
          Click and drag route waypoints to edit. Click "Done" when finished.
          <Button
            size="small"
            onClick={() => setRouteEditMode(false)}
            sx={{ ml: 2 }}
          >
            Done
          </Button>
        </Alert>
      </Collapse>
    </Paper>
  );
};